// LoginLinkPage.jsx — 第八十七批：未读提醒信里那枚次抛登录链接的落地页。
//   GET  /login/link?t=…   只取回 SPA（本页）
//   POST /api/auth/login-link { token }  ← 真正的消费，由本页发出
// 为什么要拆成两步（邮关预取会烧掉 GET 型一次性链接）见 pages/loginLink.mjs 的头注。
//
// 公开路由：它必须在**没有会话**时可达——这枚链接的全部意义就是替代登录。
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import api from '../api/client.mjs';
import { useAuth } from '../auth/AuthContext.jsx';
import { useT } from '../i18n/index.mjs';
import { tokenFromSearch, safeNext, linkOutcome } from './loginLink.mjs';

export default function LoginLinkPage() {
  const t = useT();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState('pending');   // pending|invalid|error
  // React 18 严格模式下 effect 会跑两次，而这枚令牌**只能用一次**——第二次必然
  // 401，页面就会把一次成功的登录显示成失败。用 ref 挡住第二次。
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const token = tokenFromSearch(window.location.search);
    const next = safeNext(new URLSearchParams(window.location.search).get('next'));
    if (!token) { setState('invalid'); return; }
    (async () => {
      let outcome;
      try {
        await api.loginLink(token);
        outcome = linkOutcome({ ok: true, status: 200 });
      } catch (e) {
        outcome = linkOutcome({ ok: false, status: e?.status ?? 0 });
      }
      if (outcome !== 'signedIn') { setState(outcome); return; }
      // 会话已在 cookie 里。把令牌从地址栏抹掉再离开——它已经作废，但一条留在
      // 历史记录与"复制当前网址"里的登录链接是没必要留的痕迹。
      window.history.replaceState(null, '', next);
      await refresh();
      navigate(next, { replace: true });
    })();
  }, [navigate, refresh]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Stack spacing={2} sx={{ alignItems: 'center', maxWidth: 420, width: '100%' }}>
        {state === 'pending' && (
          <>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {t('loginLink.pending')}
            </Typography>
          </>
        )}
        {state !== 'pending' && (
          <>
            <Alert severity={state === 'invalid' ? 'warning' : 'error'} sx={{ width: '100%' }}>
              {t(state === 'invalid' ? 'loginLink.invalid' : 'loginLink.error')}
            </Alert>
            <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>
              {t('loginLink.action.toLogin')}
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
}
